import React, { useRef, useCallback, useMemo, memo } from 'react';
import { Upload } from 'lucide-react';
import { NodeType } from '@/types';
import { useProjectStore } from '@/stores/projectStore';
import { projectApi } from '@/services/projectApi';
import { API_BASE } from '@/config';
import { normalizeAssetUrl } from '@/utils/assetUtils';
import { findClosestAspectRatio } from '@/utils/aspectRatioUtils';

interface UploadButtonProps {
  nodeId: string;
  nodeType?: NodeType;
  onUpdate?: (id: string, data: any) => void;
  onUploadStart?: () => void;
  onUploadEnd?: () => void;
}

const UploadButtonInner: React.FC<UploadButtonProps> = ({
  nodeId,
  nodeType,
  onUpdate,
  onUploadStart,
  onUploadEnd,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const acceptTypes = useMemo(() => {
    if (nodeType === NodeType.IMAGE_GENERATOR || nodeType === NodeType.IMAGE_EDITOR) return 'image/*';
    if (nodeType === NodeType.VIDEO_GENERATOR || nodeType === NodeType.VIDEO_COMPOSE) return 'video/*';
    if (nodeType === NodeType.AUDIO_GENERATOR) return 'audio/*';
    return 'image/*,video/*,audio/*';
  }, [nodeType]);

  const handleClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    onUploadStart?.();

    const { currentProjectId } = useProjectStore.getState();

    const processFile = (result: string) => {
      if (file.type.startsWith('image/')) {
        const img = new Image();
        img.onload = () => {
          const aspectRatio = findClosestAspectRatio(img.width, img.height);
          onUpdate?.(nodeId, {
            image: result,
            videoUri: undefined,
            audioUri: undefined,
            aspectRatio,
            isManualUpload: true,
          });
          onUploadEnd?.();
        };
        img.src = result;
      } else if (file.type.startsWith('video/')) {
        const video = document.createElement('video');
        video.onloadedmetadata = () => {
          const aspectRatio = findClosestAspectRatio(video.videoWidth, video.videoHeight);
          onUpdate?.(nodeId, {
            videoUri: result,
            image: undefined,
            audioUri: undefined,
            aspectRatio,
            isManualUpload: true,
          });
          onUploadEnd?.();
        };
        video.src = result;
      } else if (file.type.startsWith('audio/')) {
        onUpdate?.(nodeId, {
          audioUri: result,
          image: undefined,
          videoUri: undefined,
          isManualUpload: true,
        });
        onUploadEnd?.();
      }
    };

    if (currentProjectId) {
      try {
        const asset = await projectApi.uploadAsset(currentProjectId, file, {
          source: 'uploaded',
          node_id: nodeId,
          node_type: nodeType,
        });
        const assetUrl = normalizeAssetUrl(asset.url);
        processFile(assetUrl);
      } catch (err) {
        console.error('Upload to backend failed, falling back to local:', err);
        const reader = new FileReader();
        reader.onload = (ev) => {
          processFile(ev.target?.result as string);
        };
        reader.readAsDataURL(file);
      }
    } else {
      const reader = new FileReader();
      reader.onload = (ev) => {
        processFile(ev.target?.result as string);
      };
      reader.readAsDataURL(file);
    }

    e.target.value = '';
  }, [nodeId, nodeType, onUpdate, onUploadStart, onUploadEnd]);

  return (
    <div className="flex items-center justify-center nodrag">
      <input
        ref={fileInputRef}
        type="file"
        accept={acceptTypes}
        className="hidden"
        onChange={handleFileChange}
      />
      <button
        onClick={handleClick}
        onMouseDown={(e) => e.stopPropagation()}
        className="
          flex items-center gap-1.5
          px-3 py-1.5
          bg-[#262626]/95
          backdrop-blur-xl
          border border-white/10
          rounded-md
          text-xs font-medium
          text-white/80
          hover:text-white
          hover:bg-white/10
          hover:border-white/20
          transition-all
          shadow-lg
          cursor-pointer
        "
      >
        <Upload size={14} />
        <span>上传</span>
      </button>
    </div>
  );
};

export const UploadButton = memo(UploadButtonInner, (prevProps, nextProps) => {
  return (
    prevProps.nodeId === nextProps.nodeId &&
    prevProps.nodeType === nextProps.nodeType
  );
});
